import React, {
    createContext,
    ReactElement,
    ReactNode,
    useContext
} from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from './auth-context';
import { AUTH_HEADERS, baseUrl, JSON_HEADERS, TEXT_HEADERS } from "./typings";

export type ApiContextType = {
    get: (url: string) => Promise<Response>;
    post: (url: string, body?: any) => Promise<Response>;
    put: (url: string, body?: any) => Promise<Response>;
    del: (url: string) => Promise<Response>;
};

export const ApiContext = createContext<ApiContextType | undefined>(undefined);

function useApi(): ApiContextType {
    const context = useContext(ApiContext);
    if (!context) {
        throw new Error("useApi must be used within an ApiProvider");
    }
    return context;
}

const ApiProvider = (props: { children: ReactNode }): ReactElement => {
    const { token } = useAuth();
    const navigate = useNavigate();

    const get = (url: string) => {
        return fetch(baseUrl + url, {
            method: 'GET',
            headers: AUTH_HEADERS(token)
        }).then(res => {
            if (res.status === 401 || res.status === 403) {
                navigate('/login');
                return Promise.reject('Unauthorized');
            }
            return res;
        });
    }

    const post = (url: string, body?: any) => {
        return fetch(baseUrl + url, {
            method: 'POST',
            body: body ? (typeof body === 'string' ? body : JSON.stringify(body))  : undefined,
            headers: body
                ? (typeof body === 'string' ? TEXT_HEADERS(token) : JSON_HEADERS(token))
                : AUTH_HEADERS(token)
        }).then(res => {
            if (res.status === 401 || res.status === 403) {
                navigate('/login');
                return Promise.reject('Unauthorized');
            }
            return res;
        });
    }

    const put = (url: string, body?: any) => {
        return fetch(baseUrl + url, {
            method: 'PUT',
            body: body ? (typeof body === 'string' ? body : JSON.stringify(body)) : undefined,
            headers: body
                ? (typeof body === 'string' ? TEXT_HEADERS(token) : JSON_HEADERS(token))
                : AUTH_HEADERS(token)
        }).then(res => {
            if (res.status === 401 || res.status === 403) {
                navigate('/login');
                return Promise.reject();
            }
            return res;
        });
    }

    const del = (url: string) => {
        return fetch(baseUrl + url, {
            method: 'DELETE',
            headers: AUTH_HEADERS(token)
        }).then(res => {
            if (res.status === 401 || res.status === 403) {
                navigate('/login');
                return Promise.reject();
            }
            return res;
        });
    }

    return (
        <ApiContext.Provider value={{ get, post, put, del }}>
            {props.children}
        </ApiContext.Provider>
    );
};

export { ApiProvider, useApi };
